// Additive helper for SEO hreflang/canonical/locale mapping.
// Existing logic in src/components/seo/Seo.tsx remains; this module provides shared utilities.

export const LOCALES = ["fr", "en", "es", "pt"] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = "en";

// Map short locale → OG-spec full locale code.
export const OG_LOCALE_MAP: Record<Locale, string> = {
  fr: "fr_FR",
  en: "en_US",
  es: "es_ES",
  pt: "pt_BR",
};

export const SITE_ORIGIN: string =
  (typeof import.meta !== "undefined" && (import.meta as ImportMeta).env?.VITE_SITE_ORIGIN) ||
  (typeof window !== "undefined" ? window.location.origin : "https://bootpreneurs.com");

export function stripLocale(pathname: string): string {
  return pathname.replace(/^\/(fr|en|es|pt)(?=\/|$)/, "") || "/";
}

export function buildAlternates(pathname: string, origin: string = SITE_ORIGIN) {
  const basePath = stripLocale(pathname);
  const suffix = basePath === "/" ? "" : basePath;
  const alts: { hrefLang: string; href: string }[] = LOCALES.map((l) => ({
    hrefLang: l,
    href: `${origin}/${l}${suffix}`,
  }));
  alts.push({ hrefLang: "x-default", href: `${origin}/${DEFAULT_LOCALE}${suffix}` });
  return alts;
}

export function canonicalFor(pathname: string, locale: Locale, origin: string = SITE_ORIGIN): string {
  const basePath = stripLocale(pathname);
  const suffix = basePath === "/" ? "" : basePath;
  return `${origin}/${locale}${suffix}`;
}

export function ogLocaleFor(lang: string): string {
  const short = (lang || "en").slice(0, 2) as Locale;
  return OG_LOCALE_MAP[short] ?? "en_US";
}
